import { useEffect, useRef } from 'react'
import { Props as AssignmentProps } from '../Assignment'
import { UseData } from './useData'
import { useFormikContext } from 'formik'
import { ILeadFormModel } from '../../../typings'
import {
  DEFAULT_GUID_VALUE,
  UNASSIGNED_OWNER_ID,
} from '@user/components/Settings/LeadAssignmentRule/constants'
import {
  initialOwnerId,
  initialSalesTeamId,
  salesTeamIdForOwner,
} from '@user/components/Settings/LeadAssignmentRule/ownerFieldPolicy'
import { useSelector } from 'react-redux'
import { selectAllSalesTeamsUsers } from '@user/store/salesTeam/slice'

type Props = Pick<AssignmentProps, 'branchId' | 'salesTeams'> &
  Pick<UseData, 'rule' | 'salesTeamsFiltered'>

export function useEffects(props: Props) {
  const { branchId, rule, salesTeamsFiltered } = props

  const {
    setValues,
    setFieldValue,
    values: { assigneeId, salesTeamId },
  } = useFormikContext<ILeadFormModel>()
  const allTenantUsers = useSelector(selectAllSalesTeamsUsers)
  useEffect(() => {
    if (branchId) {
      const branchDefault = salesTeamsFiltered.find(
        (x) => x.branchId === branchId && x.branchDefault,
      )
      const tenantDefault = salesTeamsFiltered.find((x) => x.tenantDefault)

      const team = branchDefault
        ? branchDefault.id + ''
        : tenantDefault
          ? tenantDefault.id + ''
          : ''

      if (team) {
        setValues((v) => ({ ...v, salesTeamId: team }))
      }
    }
  }, [salesTeamsFiltered, setValues, branchId])

  useEffect(() => {
    if (rule) {
      // Not blank when the candidates span teams: an empty team empties the owner dropdown,
      // because OwnerSelect builds its options for the selected team. See initialSalesTeamId.
      const approvedTeam = initialSalesTeamId(rule.approvedSalesTeamIds)
      // The winning rule's owner, even when several are eligible. Pre-filled, never locked --
      // see initialOwnerId. Leaving it blank made a required field empty and disagreed with the
      // sales team, which defaults from the same rule.
      let ownerId = initialOwnerId(rule.approvedOwnerIds)

      if (ownerId === DEFAULT_GUID_VALUE) {
        ownerId = UNASSIGNED_OWNER_ID
      }

      // The two lists are indexed independently, so index 0 of each need not describe the same
      // person: a rule can approve teams A and B while its first owner sits only on B, and the
      // territory fallback puts ownerless territories into approvedSalesTeamIds too. Taking one
      // from each filed the lead under a team its own owner is not on, and the correction only
      // ran if the user then touched the owner field by hand.
      const salesTeamId =
        salesTeamIdForOwner(
          allTenantUsers,
          ownerId,
          approvedTeam,
          rule.approvedSalesTeamIds,
        ) ?? approvedTeam
      // A D2D territory supplies the owner and sales team only (WWM-17508). It has no sales
      // funnel, no lead status and no assignment-rule row, so applying the rule shape wholesale
      // would blank two REQUIRED fields and stamp a rule id the lead never matched.
      if (rule.isTerritoryDerived) {
        // The rule id is cleared, not left: a conventional rule may have stamped one earlier in
        // this same form session, and a territory has no rule row to point at.
        setValues((v) => ({
          ...v,
          assigneeId: ownerId || v.assigneeId,
          salesTeamId: salesTeamId || v.salesTeamId,
          automatedAssignmentRuleId: undefined,
        }))
        return
      }

      setValues((v) => ({
        ...v,
        assigneeId: ownerId,
        salesTeamId,
        salesFunnelId: rule.salesFunnelId,
        salesFunnelStageId: rule.leadStatusId,
        automatedAssignmentRuleId: rule.automatedAssignmentId,
      }))
    }
  }, [rule, setFieldValue, setValues])

  // Re-resolve the team once the membership list arrives.
  //
  // The effect above runs when the rule lands, and that can be before salesTeamsUsers has
  // loaded: salesTeamIdForOwner then sees no memberships for the owner, returns null, and the
  // team falls back to approvedSalesTeamIds[0] -- the wrong-team case the pairing exists to
  // prevent, with nothing to correct it once the users do arrive.
  //
  // It lives in its own effect rather than as a dependency of that one, which assigns owner,
  // team, funnel, stage and rule id wholesale: re-running it on a late-loading user list would
  // throw away anything the person had changed in between.
  //
  // Once per owner, not on every team edit.
  //
  // salesTeamId is read through a ref and is NOT a dependency. Listing it would make this a
  // correction that runs forever: a rule approving teams A and B whose assignee sits only on B
  // would snap a deliberate, rule-approved choice of A straight back to B, every time the user
  // made it. Moving the team when the OWNER changes is onChangeOwner's job; all this effect
  // owes anyone is the one correction that the late-arriving user list makes possible.
  const salesTeamIdRef = useRef(salesTeamId)
  salesTeamIdRef.current = salesTeamId

  const resolvedForRef = useRef<string | null>(null)

  useEffect(() => {
    if (!rule || !assigneeId || !allTenantUsers?.length) return

    // A territory-derived result has no rule id, so it is keyed by its own marker. Either way
    // the pair identifies "this assignment, this owner", which is what gets one correction.
    const key = `${rule.automatedAssignmentId ?? 'territory'}:${assigneeId}`
    if (resolvedForRef.current === key) return

    resolvedForRef.current = key

    const resolved = salesTeamIdForOwner(
      allTenantUsers,
      assigneeId,
      salesTeamIdRef.current,
      rule.approvedSalesTeamIds,
    )

    if (resolved) setFieldValue('salesTeamId', resolved)
  }, [allTenantUsers, assigneeId, rule, setFieldValue])

  useEffect(() => {
    if (salesTeamsFiltered && salesTeamsFiltered.length === 1) {
      setFieldValue('salesTeamId', salesTeamsFiltered[0].id)
    }
  }, [salesTeamsFiltered, setFieldValue])
}
